import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import api from '../api';

const MAX_APPLICATIONS = 20;

function formatMoney(cents, currency = 'usd') {
  if (cents == null || Number.isNaN(Number(cents))) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase(),
    }).format(Number(cents) / 100);
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency || ''}`.trim();
  }
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

/**
 * Read-only screening billing for the signed-in landlord.
 * Uses existing GET /api/applications/all + GET /api/billing/screeningOrders?applicationId=.
 * No SaaS subscription Checkout.
 */
export default function AccountBillingDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [loadNote, setLoadNote] = useState(null);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setLoadNote(null);
      setOrders([]);

      try {
        const appsRes = await api.get('/api/applications/all');
        const apps = Array.isArray(appsRes.data) ? appsRes.data : [];
        const slice = apps.slice(0, MAX_APPLICATIONS);

        if (slice.length === 0) {
          if (!cancelled) {
            setOrders([]);
            setLoadNote('No rental applications yet — screening charges will appear here when you run checks.');
          }
          return;
        }

        const results = await Promise.allSettled(
          slice.map((app) =>
            api.get('/api/billing/screeningOrders', {
              params: { applicationId: app.id },
            })
          )
        );

        if (cancelled) return;

        const merged = [];
        let failed = 0;
        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && Array.isArray(result.value.data)) {
            result.value.data.forEach((order) => {
              merged.push({
                ...order,
                _applicationLabel:
                  slice[index]?.applicantName ||
                  slice[index]?.applicationReference ||
                  slice[index]?.id,
              });
            });
          } else if (result.status === 'rejected') {
            failed += 1;
          }
        });

        merged.sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tb - ta;
        });

        setOrders(merged);
        if (failed > 0 && merged.length === 0) {
          setError('Could not load screening orders for your applications.');
        } else if (apps.length > MAX_APPLICATIONS) {
          setLoadNote(
            `Showing screening orders for your ${MAX_APPLICATIONS} most recently listed applications (${apps.length} total).`
          );
        }
      } catch (err) {
        console.error('❌ Billing panel load failed:', err);
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.message ||
              'Could not load screening billing.'
          );
          setOrders([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Billing</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Charges for tenant screening checks on your rental applications. This list is read-only.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          ZenRentFlow product subscription (plan, invoices, payment method) is not available yet —
          that will land in a later release. Screening checkout still runs from each application.
        </Alert>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && loadNote && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {loadNote}
          </Alert>
        )}

        {!loading && !error && orders.length === 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              No screening charges yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When you run a background or credit check from Applications, the order and amount
              will show up here.
            </Typography>
          </Box>
        )}

        {!loading && orders.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Application</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>{formatDate(order.createdAt)}</TableCell>
                  <TableCell>{order._applicationLabel || order.applicationId || '—'}</TableCell>
                  <TableCell>{order.status || '—'}</TableCell>
                  <TableCell>{order.provider || order.providerId || '—'}</TableCell>
                  <TableCell align="right">
                    {formatMoney(
                      order.priceCents ?? order.amountCents ?? order.amount,
                      order.currency
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
